import React, { useEffect, useState, useRef } from 'react';
import { isMobile } from 'react-device-detect';
import { AutoComplete, Badge } from 'rsuite';

/**
 * Check if a card is double-faced (transform, modal DFC, etc.)
 */
const isDoubleFaced = (card) => card?.card_faces && !card?.image_uris;

const SEARCH_ITEM_HEIGHT = isMobile ? 48 : 28;
const SEARCH_ITEM_GAP = isMobile ? 12 : 8;
const SEARCH_FONT_SIZE = isMobile ? 16 : 14;
const SEARCH_SUBTEXT_SIZE = isMobile ? 14 : 11;
const SEARCH_BADGE_FONT_SIZE = isMobile ? 11 : 10;
const SEARCH_ITEM_PADDING = isMobile ? 12 : 4;

/**
 * Renders a react auto complete search bar.
 * @param {object} props Properties containing term, setTerm, and retrieve. Retrieve must be a promise
 * @returns React Component
 */
const SearchBar = (props) => {
  const [data, setData] = useState([]);
  const [cards, setCards] = useState([]);
  const debounceTimer = useRef(null);

  const term = props.term;
  const setTerm = props.setTerm;
  const retrieve = props.retrieve;
  const onSelect = props.onSelect;
  const placeholder = props.placeholder;

  useEffect(() => {
    // Clear the previous timeout
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    /**
     * Retrieves cards matching the provided terms.
     */
    const fetchData = async () => {
      // Only fetch if term doesn't contain // (double-faced card names break the URL)
      // and term is at least 2 characters
      if (term && term.length >= 2 && !term.includes('//')) {
        try {
          const results = await retrieve(term);
          const formatedResults = JSON.parse(results);
          const cardNames = [];

          formatedResults.forEach((result) => {
            cardNames.push(result.name);
          });

          setData(cardNames);
          setCards(formatedResults);
        } catch(e) {
          console.error(e);
        }
      } else if (!term || term.length < 2) {
        setData([]);
        setCards([]);
      }
      // If term contains //, keep the existing cards data (don't clear or refetch)
    }

    // Debounce API calls by 400ms to avoid excessive requests while typing
    debounceTimer.current = setTimeout(fetchData, 400);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [term, retrieve]);

  const handleSelect = (value) => {
    // Find the card by name and call onSelect directly with the card object
    const selectedCard = cards.find(card => card.name === value);
    if (selectedCard) {
      onSelect(selectedCard);
    }
  };

  const renderMenuItem = (label, item) => {
    const card = cards.find(c => c.name === item.value);
    const thumb = card?.image_uris?.small || card?.card_faces?.[0]?.image_uris?.small;
    const isDFC = card && isDoubleFaced(card);

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: SEARCH_ITEM_GAP, padding: `${SEARCH_ITEM_PADDING}px 0`, minHeight: isMobile ? SEARCH_ITEM_HEIGHT : 'auto' }}>
        {thumb && (
          <img src={thumb} alt={card.name} style={{ height: SEARCH_ITEM_HEIGHT, borderRadius: 2, flexShrink: 0 }} />
        )}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: SEARCH_FONT_SIZE }}>
            {label}
            {isDFC && (
              <Badge content="DFC" style={{ backgroundColor: '#6c757d', fontSize: `${SEARCH_BADGE_FONT_SIZE}px` }} />
            )}
          </div>
          {card?.set_name && (
            <div style={{ fontSize: SEARCH_SUBTEXT_SIZE, color: '#aaa' }}>{card.set_name}</div>
          )}
        </div>
      </div>
    );
  };

  // Prevent mousedown/touchstart on the dropdown menu (including its scrollbar) from
  // blurring the input, which RSuite uses as the signal to close the dropdown.
  const renderMenu = (menu) => (
    <div
      onMouseDown={(e) => e.preventDefault()}
      onTouchStart={(e) => {
        if (e.target.tagName === 'DIV' || e.target.tagName === 'IMG') {
          e.preventDefault();
        }
      }}
    >
      {menu}
    </div>
  );

  return (
    <AutoComplete
      placeholder={placeholder}
      data={data}
      onChange={setTerm}
      onSelect={handleSelect}
      renderMenuItem={renderMenuItem}
      renderMenu={renderMenu}
    />
  );
}

export default SearchBar;